import type { BoxProps, ButtonProps, MantineThemeOverride } from '@mantine/core'
import { Box, Button, Group, LoadingOverlay, MantineThemeProvider, Progress, Text } from '@mantine/core'
import type { DropzoneProps, FileRejection } from '@mantine/dropzone'
import { Dropzone, IMAGE_MIME_TYPE } from '@mantine/dropzone'
import { notifications } from '@mantine/notifications'
import type { IconProps } from '@tabler/icons-react'
import { IconArrowDown, IconCloudUpload } from '@tabler/icons-react'
import { inflect } from 'inflection'
import type { ComponentType, ReactNode } from 'react'
import { useRef } from 'react'

import type { EmptyStateProps } from '@/components/EmptyState'
import { EmptyState } from '@/components/EmptyState'
import { InputLabel } from '@/components/InputLabel'
import { theme } from '@/configs/theme'
import * as commonClasses from '@/styles/componentCommons.css'
import * as classes from '@/styles/FileUpload.css'

// Dropzone renders its LoadingOverlay internally and exposes no prop or Styles API slot for it,
// so its z-index can only be reached through the theme. Mantine's default of 400 would sit above
// the modals this component is used in. Module-level so the provider's theme merge stays memoized.
const dropzoneOverlayTheme: MantineThemeOverride = {
  components: {
    LoadingOverlay: LoadingOverlay.extend({ defaultProps: { zIndex: 10 } }),
  },
}

const pdfMimeType = 'application/pdf'
const markdownMimeType = 'text/markdown'
// Some OSes/browsers report `.md` as the legacy `text/x-markdown`; accept both.
const markdownAltMimeType = 'text/x-markdown'
const markdownMimeTypes = [markdownMimeType, markdownAltMimeType]

export const textMimeTypes = ['text/plain', ...markdownMimeTypes]

export const videoMimeTypes = [
  'video/mp4',
  'video/quicktime', // .mov
]

export const audioMimeTypes = [
  'audio/mpeg', // .mp3
  'audio/wav', // .wav
  'audio/x-m4a', // .m4a
  'audio/mp4', // .m4a (alternative)
]

export const imageMimeTypes = ['image/png', 'image/jpeg', 'image/tiff']

export const tabularMimeTypes = [
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
  'application/vnd.ms-excel.sheet.macroEnabled.12', // .xlsm
  'text/csv', // .csv
  'application/csv', // .csv (some browsers)
]

export const acceptedFileUploadText =
  'Drag and drop files or browse for them using the button below.\r\nValid file types include: png, jpg, tiff, mp4, mov, mp3, wav, m4a, txt, md, pdf, pptx, docx, csv and xlsx.'

// Generic fallback used by `defaultOnReject` when the rejection code is unknown.
export const rejectedFileUploadText = "This file can't be uploaded. Check that the file type and size are supported."

export const needsExtractionMimeTypes = [
  pdfMimeType,
  'text/plain',
  ...markdownMimeTypes,
  // docx
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  // pptx
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  // video files
  ...videoMimeTypes,
  // audio files
  ...audioMimeTypes,
  // image files
  ...imageMimeTypes,
  // tabular files (csv/xlsx) — backend converts to record-oriented markdown for RAG
  ...tabularMimeTypes,
]
export const supportsProgressMimeTypes = [pdfMimeType, ...videoMimeTypes, ...audioMimeTypes]
export const needsMetadataGenerationMimeTypes = [...videoMimeTypes, ...audioMimeTypes, ...imageMimeTypes]

// Using set to remove duplicates
export const contentUploadMimeTypes = [...new Set([...textMimeTypes, ...needsExtractionMimeTypes])]
// For media files, we use only MIME types (extensions cause console warnings in Dropzone)
export const mediaUploadMimeTypes = [...new Set([...videoMimeTypes, ...audioMimeTypes, ...imageMimeTypes])]
// Unified upload mime types for all file types (text, media, office docs)
export const allUploadMimeTypes = [...new Set([...contentUploadMimeTypes, ...mediaUploadMimeTypes])]

// Object form (vs. flat MIME list) lets the dropzone match by extension when
// `file.type` is empty — common for `.md` on Windows/Linux.
export const uploadAcceptByMime: Record<string, string[]> = {
  'text/plain': ['.txt'],
  [markdownMimeType]: ['.md', '.markdown'],
  [markdownAltMimeType]: ['.md', '.markdown'],
  [pdfMimeType]: ['.pdf'],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': ['.pptx'],
  'video/mp4': ['.mp4'],
  'video/quicktime': ['.mov'],
  'audio/mpeg': ['.mp3'],
  'audio/wav': ['.wav'],
  'audio/x-m4a': ['.m4a'],
  'audio/mp4': ['.m4a'],
  'image/png': ['.png'],
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/tiff': ['.tif', '.tiff'],
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
  'application/vnd.ms-excel.sheet.macroEnabled.12': ['.xlsm'],
  'text/csv': ['.csv'],
  'application/csv': ['.csv'],
}

export const allUploadAccept: Record<string, string[]> = Object.fromEntries(
  allUploadMimeTypes.map(mime => [mime, uploadAcceptByMime[mime] ?? []]),
)

// Media-mode primary files reject text/markdown and csv/spreadsheet sources
// (manual entry is hidden too) — only media and document files are valid.
const excludedPrimaryMediaMimeTypes = [...textMimeTypes, ...tabularMimeTypes]
export const primaryMediaUploadMimeTypes = allUploadMimeTypes.filter(
  mime => !excludedPrimaryMediaMimeTypes.includes(mime),
)
export const primaryMediaUploadAccept: Record<string, string[]> = Object.fromEntries(
  primaryMediaUploadMimeTypes.map(mime => [mime, uploadAcceptByMime[mime] ?? []]),
)

// TODO: Svgs were removed as a precaution, there was potentially an issue rendering them with iOS 17, though we didn't fully investigate.
// See SME-152 https://sidekickwellness.atlassian.net/browse/SME-152?atlOrigin=eyJpIjoiMzUwOWY5ZTU4M2NjNDM5OGE3NGU5M2ZjNGQ0MGNiOGQiLCJwIjoiaiJ9
export const ALLOWED_IMAGE_TYPES = IMAGE_MIME_TYPE.filter(type => type !== 'image/svg+xml')

export type UploadPhase = 'idle' | 'uploading' | 'processing'

function describeRejection(rejection: FileRejection): string {
  const code = rejection.errors[0]?.code
  switch (code) {
    case 'file-invalid-type':
      return 'unsupported file type'
    case 'file-too-large':
      return 'file is too large'
    case 'file-too-small':
      return 'file is too small'
    case 'too-many-files':
      return 'too many files at once'
    default:
      return rejection.errors[0]?.message ?? rejectedFileUploadText
  }
}

// Without this, dropzone rejections are silent. Override via the `onReject` prop.
function defaultOnReject(rejections: FileRejection[]) {
  if (!rejections.length) return
  const [first] = rejections
  const reason = describeRejection(first)
  const remainder = rejections.length > 1 ? ` (and ${rejections.length - 1} more)` : ''
  notifications.show({
    title: 'Upload failed',
    message: `Couldn't add "${first.file.name}"${remainder}: ${reason}.`,
    color: 'red',
    classNames: commonClasses.notificationFilled,
    position: 'bottom-left',
  })
}

export interface FileUploadProps extends Omit<DropzoneProps, 'openRef' | 'title'> {
  WrapperProps?: BoxProps
  EmptyStateProps?: Partial<EmptyStateProps>
  titles?: {
    idle?: string
    accept?: string
  }
  icons?: {
    idle?: ComponentType<IconProps>
    accept?: ComponentType<IconProps>
  }
  hint?: ReactNode
  hints?: {
    idle?: ReactNode
    accept?: ReactNode
  }
  primaryActionLabel?: string
  secondaryActionLabel?: string
  onSecondaryActionClick?: () => void
  tertiaryActionLabel?: string
  onTertiaryActionClick?: () => void
  actionButtonProps?: ButtonProps
  actionHidden?: boolean
  BottomSlot?: ReactNode
  label?: string
  error?: string
  description?: string
  withAsterisk?: boolean
  // Upload phase tracking
  uploadPhase?: UploadPhase
  uploadProgress?: number
  processProgress?: number
  // Cancellation support
  onCancelClick?: () => void
  showCancelButton?: boolean
  /** @deprecated Use uploadPhase instead */
  processing?: boolean
}

export function FileUpload(props: FileUploadProps) {
  const {
    WrapperProps,
    titles,
    hint,
    hints,
    icons,
    error,
    description,
    label,
    maxFiles = 10,
    withAsterisk,
    primaryActionLabel,
    actionButtonProps,
    actionHidden,
    BottomSlot = null,
    EmptyStateProps,
    loading,
    uploadPhase = 'idle',
    uploadProgress = 0,
    processProgress = 0,
    processing, // deprecated, for backwards compatibility
    secondaryActionLabel,
    onSecondaryActionClick,
    tertiaryActionLabel,
    onTertiaryActionClick,

    // Cancellation support
    onCancelClick,
    showCancelButton,
    disabled,
    onReject,
    ...DropzoneProps
  } = props

  // Determine active phase (support deprecated 'processing' prop)
  const isUploading = uploadPhase === 'uploading'
  const isProcessing = uploadPhase === 'processing' || processing
  const showProgressOverlay = isUploading || isProcessing

  // Determine which progress value and label to show
  const progressValue = isUploading ? uploadProgress : processProgress
  const progressLabel = isUploading ? 'Uploading file...' : 'Processing content...'

  const { idle: IconIdle = IconCloudUpload, accept: IconAccept = IconArrowDown } = icons ?? {}

  const hasControls = !actionHidden
  const defaultHint = hint ?? `Drag & drop ${inflect('file', maxFiles)} here to upload.`

  const openRef = useRef<() => void>(null)

  const idleHintContent = hints?.idle ?? defaultHint

  // Reject state reuses Idle: dragenter only exposes MIME, which falsely rejects
  // valid `.md` drops. Real validation runs at drop time via `accept` + `onReject`.
  const idleEmptyState = (
    <EmptyState
      size="sm"
      title={titles?.idle ?? `Upload ${inflect('File', maxFiles)}`}
      description={idleHintContent}
      Icon={IconIdle}
      DescriptionProps={{ maw: '100%' }}
      {...EmptyStateProps}
    />
  )

  return (
    <Box>
      <Box
        {...WrapperProps}
        className={theme.cx(classes.wrapper, WrapperProps?.className, hasControls && classes.wrapperWControls)}>
        {label && <InputLabel label={label} withAsterisk={withAsterisk} />}

        <Box className={classes.dropzoneWrapper}>
          {showProgressOverlay && (
            <Box className={classes.progressOverlay}>
              <Box style={{ width: '100%', margin: 'auto' }}>
                <Text size="sm" fw={500} mb="sm" style={{ alignSelf: 'flex-start' }}>
                  {progressLabel}
                </Text>
                <Group gap="lg" w="100%" align="center">
                  <Progress
                    value={progressValue}
                    size="xl"
                    radius="md"
                    color={theme.colors.primaryColors[6]}
                    animated
                    style={{ flex: 1, height: '12px' }}
                  />
                  <Text size="lg" fw={600} c="dimmed" style={{ minWidth: 50, textAlign: 'right' }}>
                    {Math.round(progressValue)}%
                  </Text>
                </Group>
              </Box>
              {showCancelButton && onCancelClick && (
                <Button size="sm" variant="subtle" color="red" onClick={onCancelClick} style={{ marginTop: '12px' }}>
                  Cancel
                </Button>
              )}
            </Box>
          )}
          <MantineThemeProvider theme={dropzoneOverlayTheme}>
            <Dropzone
              radius="md"
              maxFiles={maxFiles}
              // Force legacy <input type="file"> picker. Mantine's FS Access API
              // default hides `.md` and other text-y extensions on macOS Chrome.
              // Override via DropzoneProps (the spread wins).
              useFsAccessApi={false}
              {...DropzoneProps}
              disabled={disabled}
              loading={loading && !showProgressOverlay}
              openRef={openRef}
              onReject={onReject ?? defaultOnReject}
              classNames={{
                root: theme.cx(classes.dropzone, DropzoneProps.className, !!error && classes.dropzoneWError),
                inner: theme.cx(
                  classes.dropzoneInner,
                  hasControls && classes.dropzoneInnerWControls,
                  DropzoneProps?.activateOnClick === false && classes.dropzoneInnerWPointerEvents,
                ),
              }}>
              <Dropzone.Idle>{idleEmptyState}</Dropzone.Idle>

              <Dropzone.Accept>
                <EmptyState
                  size="sm"
                  title={titles?.accept ?? `Drop ${inflect('File', maxFiles)} Here...`}
                  description={hints?.accept ?? defaultHint}
                  Icon={IconAccept}
                  IconProps={{ color: theme.colors.primaryColors[6] }}
                  DescriptionProps={{ maw: '100%' }}
                  {...EmptyStateProps}
                />
              </Dropzone.Accept>

              <Dropzone.Reject>{idleEmptyState}</Dropzone.Reject>

              {BottomSlot}

              {hasControls && (
                <Group gap="xl" className={theme.cx(classes.controlsWrapper, loading && classes.controlsWhileLoading)}>
                  <Button
                    className={classes.selectControl}
                    onClick={() => openRef.current?.()}
                    disabled={disabled}
                    {...actionButtonProps}>
                    {primaryActionLabel ?? `Select ${inflect('File', maxFiles)}`}
                  </Button>

                  {secondaryActionLabel && (
                    <Button
                      size="md"
                      variant="light"
                      className={theme.cx(classes.selectControl, classes.secondaryAction)}
                      onClick={onSecondaryActionClick}
                      disabled={disabled}
                      {...actionButtonProps}>
                      {secondaryActionLabel}
                    </Button>
                  )}

                  {tertiaryActionLabel && (
                    <Button
                      size="md"
                      variant="light"
                      className={theme.cx(classes.selectControl, classes.secondaryAction)}
                      onClick={onTertiaryActionClick}
                      disabled={disabled}
                      {...actionButtonProps}>
                      {tertiaryActionLabel}
                    </Button>
                  )}
                </Group>
              )}
            </Dropzone>
          </MantineThemeProvider>
        </Box>
      </Box>

      {error ? (
        <Text className={classes.error}>{error}</Text>
      ) : description ? (
        <Text c="dimmed" className={classes.description}>
          {description}
        </Text>
      ) : null}
    </Box>
  )
}
